import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { TrendingUp, Loader2, Eye, EyeOff } from 'lucide-react';
import { MOTTO } from '../config/brand';

export default function Login() {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    
    const from = location.state?.from?.pathname || '/dashboard';

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        
        const result = await login(email, password);
        
        if (result.success) {
            // Redirect based on role and status
            if (result.user.role === 'admin') {
                navigate('/admin', { replace: true });
            } else if (result.user.status === 'pending') {
                navigate('/pending-approval', { replace: true });
            } else if (result.user.status === 'rejected') {
                navigate('/rejected', { replace: true });
            } else {
                navigate(from, { replace: true });
            }
        } else {
            setError(result.error);
        }
        
        setLoading(false);
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4 relative" style={{ backgroundColor: 'var(--background-base)' }}>
            {/* Top Bar with Language Switcher */}
            <div className="absolute top-4 right-4 z-20">
                <LanguageSwitcher />
            </div>
            
            {/* Background effect */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl opacity-20" style={{ backgroundColor: 'var(--primary-color)' }}></div>
            </div>
            
            <div className="relative z-10 w-full max-w-md">
                {/* Logo */}
                <Link to="/" className="flex items-center justify-center gap-3 mb-8" data-testid="logo-link">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center gold-glow" style={{ backgroundColor: 'var(--primary-color)' }}>
                        <TrendingUp className="w-6 h-6" style={{ color: 'var(--primary-text)' }} />
                    </div>
                    <div>
                        <p className="font-bold text-xl tracking-tight" style={{ fontFamily: "'Outfit', sans-serif", color: 'var(--text-primary)' }}>
                            NIKAYOFX
                        </p>
                        <p className="text-xs tracking-widest" style={{ color: 'var(--text-muted)' }}>
                            PTG
                        </p>
                    </div>
                </Link>
                <p className="text-center -mt-5 mb-6 text-xs italic font-semibold tracking-[0.18em]" style={{ fontFamily: "'Playfair Display', serif", color: 'var(--primary-color)' }} data-testid="brand-motto">
                    “{MOTTO}”
                </p>
                
                {/* Login Card */}
                <div className="card-surface p-8" data-testid="login-form-container">
                    <div className="text-center mb-8">
                        <h1 className="text-2xl font-bold tracking-tight mb-2" style={{ fontFamily: "'Outfit', sans-serif", color: 'var(--text-primary)' }}>
                            {t('auth.login.title')}
                        </h1>
                        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                            {t('auth.login.subtitle')}
                        </p>
                    </div>
                    
                    {error && (
                        <div className="mb-6 p-4 rounded-lg text-sm" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)', color: 'var(--accent-danger)' }} data-testid="login-error">
                            {error}
                        </div>
                    )}
                    
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="space-y-2">
                            <Label htmlFor="email" style={{ color: 'var(--text-secondary)' }}>{t('auth.login.email')}</Label>
                            <Input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                required
                                className="input-field w-full ltr-only"
                                data-testid="login-email-input"
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label htmlFor="password" style={{ color: 'var(--text-secondary)' }}>{t('auth.login.password')}</Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    required
                                    className="input-field w-full pr-10 ltr-only"
                                    data-testid="login-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2"
                                    style={{ color: 'var(--text-muted)' }}
                                    data-testid="toggle-password-visibility"
                                >
                                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>
                        
                        <Button
                            type="submit"
                            className="btn-primary w-full"
                            disabled={loading}
                            data-testid="login-submit-btn"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    {t('auth.login.submitting')}
                                </>
                            ) : (
                                t('auth.login.submit')
                            )}
                        </Button>
                    </form>
                    
                    <p className="mt-6 text-center text-sm" style={{ color: 'var(--text-secondary)' }}>
                        {t('auth.login.noAccount')}{' '}
                        <Link to="/register" className="font-medium hover:underline" style={{ color: 'var(--primary-color)' }} data-testid="register-link">
                            {t('auth.login.signUpLink')}
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
